import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import DefaultLayout from '../../components/DefaultLayout';
import Filter from '../../components/Filter';
import {
  Button,
  Checkbox,
  Dropdown,
  Icon,
  Input,
  Select,
  SelectOption,
  Table,
  Tabs,
} from '../../../ui';
import { ProductAmount, ProductData } from 'types/ProductData';
import { ADMIN_BASE, AdminRoute } from 'enums/adminRoutes';
import { CategorySelector } from '../../../ui/Select/Selectors/CategorySelectors';
import { StyledButtonList } from '../../../styles/buttonList';
import { DrawerShareProduct } from '../../../ui/Drawer/Drawers/DrawerShareProduct';
import { DrawerCreateProduct } from '../../../ui/Drawer/Drawers/DrawerCreateProduct';
import { ShareProductForm } from '../../../ui/Form/products/ShareProductForm';
import { useAuth } from '../../../hooks/useAuth';
import { StyledMobileCategoryWrapper, StyledProductsPage } from './style';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useSearchParams } from 'react-router-dom';
import { DrawerMansLauksProducts } from 'ui/Drawer/Drawers/DrawerMansLauksProducts';
import { Pagination } from '../../../types/Pagination';
import { message, Modal } from 'antd';
import { CategoryData } from 'types/CategoryData';
import {
  StyledMobileHeaderWrapper,
  StyledMobilePageTitle,
} from 'admin/components/MobileLayout/style';
import MobileList from 'admin/components/MobileLayout/MobileList';
import AdminMobileFilterDrawer from 'ui/Drawer/Drawers/AdminMobileFilterDrawer';
import { useBreakpoint } from 'utils/useBreakpoint';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import { FarmData } from 'types/FarmData';
import { DownOutlined } from '@ant-design/icons';
import { MenuProps } from 'antd/es/menu';
import type { MenuInfo } from 'rc-menu/lib/interface';
import { hasAbility } from 'utils/userHelper';
import { features } from 'config/config';

const MANS_LAUKS_APP_NAME = 'NovadaGarša';

export interface linkTokenResponse {
  farm?: number;
  linkSuccessful: boolean;
  linkError?: boolean;
  exception?: string;
}

export const Products = () => {
  const [paginationStatus, setPaginationStatus] = useState<Pagination>({});
  const [advancedSearch, setAdvancedSearch] = useState({});
  const [mansLauksProducts, setMansLauksProducts] = useState([]);
  const [returnedProducts, setReturnedProducts] = useState([]);
  const [farms, setFarms] = useState([]);
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const [isOpenCreateDrawer, setIsOpenCreteDrawer] = useState(false);
  const [isOpenMansLauksDrawer, setIsOpenMansLauksDrawer] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductData>();
  const [reload, setReload] = useState<number>(0);
  const [isProductMobileFilterOpen, setIsProductMobileFilterOpen] =
    useState<boolean>(false);
  const { user, refetchUser } = useAuth();
  const { isLgUp } = useBreakpoint();
  const intl = useIntl();
  const farmId = user?.farmId;
  const frontendUrl = window.runConfig.frontendUrl;
  const mansLauksLink = window.runConfig.mansLauksUrl;
  const isAdmin = user?.isAdmin;
  const isMansLauksEnabled = features.mansLauks;

  const mansLauksUrl = new URL('/tokens/create/', mansLauksLink);
  mansLauksUrl.search = new URLSearchParams({
    name: MANS_LAUKS_APP_NAME,
    'return-url': `${frontendUrl}${ADMIN_BASE}${AdminRoute.PRODUCTS}`,
  }).toString();
  const mansLauksHref = mansLauksUrl.toString();

  const stableFilter = React.useMemo(() => {
    const farm = isAdmin ? null : user?.farmId;
    return { ...advancedSearch, farm };
  }, [advancedSearch, user?.farmId, isAdmin]);

  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get('code');

  const handleGetProducts = React.useCallback(
    (data: any) => {
      setReturnedProducts(data);
    },
    [setReturnedProducts]
  );

  const handleResponse = React.useCallback(
    (data: any) => {
      setFarms(data.farms);
    },
    [setFarms]
  );

  const handleCloseMansLauksDrawer = () => {
    setIsOpenMansLauksDrawer(!isOpenMansLauksDrawer);
  };

  const handleMansLauksClick = () => {
    if (user?.isMansLauksLinked) {
      setIsOpenMansLauksDrawer(true);
    } else if (user?.hasMansLauksToken) {
      linkToken({});
    } else {
      window.location.href = mansLauksHref;
    }
  };

  const handleMansLauksUnlinkClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'products.mans_lauks_unlink' }),
      content: intl.formatMessage({ id: 'products.mans_lauks_unlink_confirm' }),
      okText: intl.formatMessage({ id: 'general.yes' }),
      cancelText: intl.formatMessage({ id: 'general.no' }),
      onOk: () => unlinkMansLauks(),
    });
  };

  const { appendData: getMansLauksProducts } = useQueryApiClient({
    request: {
      url: `/api/v2/manslauks/${farmId}/products`,
      method: 'GET',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setMansLauksProducts(response);
    },
    onError: () => {
      message.error({
        content: intl.formatMessage({
          id: 'message.manslauks_connection_lost',
        }),
        key: 'manslauks-connection-lost',
      });
      setMansLauksProducts([]);
      refetchUser();
    },
  });

  const { appendData: unlinkMansLauks } = useQueryApiClient({
    request: {
      url: `/api/v2/manslauks/${farmId}/unlink`,
      method: 'POST',
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'message.manslauks_unlinked' }));
      setMansLauksProducts([]);
      refetchUser();
    },
    onError: () =>
      message.error(intl.formatMessage({ id: 'message.failed_unlink' })),
  });

  const { appendData: linkToken } = useQueryApiClient({
    request: {
      url: `/api/v2/manslauks/${farmId}/link`,
      method: 'POST',
    },
    onSuccess: (response: linkTokenResponse) => {
      if (response.linkSuccessful) {
        getMansLauksProducts();
        setIsOpenMansLauksDrawer(true);
        refetchUser();
      } else if (response.linkError) {
        message.error(
          intl.formatMessage({ id: 'message.manslauks_reg_mismatch' })
        );
      } else {
        message.error(intl.formatMessage({ id: 'message.failed_link' }));
      }
    },
    onError: () =>
      message.error(intl.formatMessage({ id: 'message.failed_link' })),
  });

  useEffect(() => {
    if (!isMansLauksEnabled) {
      return;
    }

    if (code) {
      if (!user?.isMansLauksLinked && farmId) {
        linkToken({ code });
      }

      searchParams.delete('code');
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (user?.isMansLauksLinked) {
      getMansLauksProducts();
    }
  }, [code, user?.isMansLauksLinked, isMansLauksEnabled]);

  const { appendData: deleteProd } = useQueryApiClient({
    request: {
      url: `api/v2/products/:id`,
      method: 'DELETE',
    },
  });

  const handleOpenCreateDrawer = () => {
    setIsOpenCreteDrawer(true);
  };

  const handleCloseCreateDrawer = () => {
    setIsOpenCreteDrawer(false);
    setSelectedProduct(undefined);
  };

  const handleOpenDrawer = (product: ProductData) => {
    setSelectedProduct(product);
    setIsOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setSelectedProduct(undefined);
    setIsOpenDrawer(false);
  };

  const renderBooleanCheckmarks = (value: boolean) => {
    return <Checkbox checked={value} />;
  };

  const renderAvailableAmounts = (
    productAmounts: ProductAmount[] | undefined,
    record: ProductData
  ) => {
    const emarketAmount = record.emarket
      ? (productAmounts?.find((amount) => amount.type === 'EMARKET')
          ?.storage_stock_count ?? 0)
      : 0;
    const wholesaleAmount = record.wholesale
      ? (productAmounts?.find((amount) => amount.type !== 'EMARKET')
          ?.storage_stock_count ?? 0)
      : 0;

    return `${emarketAmount}/${wholesaleAmount}`;
  };

  const resetFilter = () => {
    setAdvancedSearch({});
    setReload((prev) => prev + 1);
  };

  const columns = [
    ...(isAdmin
      ? [
          {
            title: intl.formatMessage({ id: 'general.farms' }),
            dataIndex: 'brand',
            render: (value: string) => value,
          },
        ]
      : []),
    {
      title: intl.formatMessage({ id: 'general.name' }),
      dataIndex: 'name',
      render: (value: string) => value,
    },
    {
      title: intl.formatMessage({ id: 'products.category' }),
      dataIndex: 'subcategory',
      render: (value: CategoryData) => value?.name ?? '',
    },
    {
      title: intl.formatMessage({ id: 'products.visibility' }),
      dataIndex: 'hidden',
      render: (value: boolean) => renderBooleanCheckmarks(!value),
    },
    {
      title: intl.formatMessage({ id: 'products.e_market' }),
      dataIndex: 'emarket',
      render: (value: boolean) => renderBooleanCheckmarks(value),
    },
    {
      title: intl.formatMessage({ id: 'general.wholesale' }),
      dataIndex: 'wholesale',
      render: (value: boolean) => renderBooleanCheckmarks(value),
    },
    {
      title: intl.formatMessage({ id: 'products.available_amount' }),
      dataIndex: 'product_amounts',
      render: (productAmounts: ProductAmount[], record: ProductData) =>
        renderAvailableAmounts(productAmounts, record),
    },
    {
      title: intl.formatMessage({ id: 'products.share' }),
      render: (record: ProductData) => (
        <img
          onClick={() => handleOpenDrawer(record)}
          src={'/share_icon.svg'}
          alt="Share"
          className={'pointer'}
        />
      ),
    },
    {
      title: intl.formatMessage({ id: 'products.actions' }),
      render: (record: ProductData) => (
        <div className={'actions-container'}>
          <Button
            type="primary"
            onClick={() => {
              setSelectedProduct(record);
              setIsOpenCreteDrawer(true);
            }}
            label={intl.formatMessage({ id: 'products.edit' })}
            ability={'edit_product'}
          />
          <Button
            type="default"
            onClick={() => {
              if (
                window.confirm(
                  `${intl.formatMessage({ id: 'confirmation.delete' })} : ${
                    record.name
                  }`
                )
              ) {
                deleteProd(null, { id: record.id });
                setReload((old) => old + 1);
              }
            }}
            label={intl.formatMessage({ id: 'products.delete' })}
            ability={'edit_product'}
          />
        </div>
      ),
    },
  ];

  const mobileItems = {
    coverFields: [
      {
        name: 'name',
        render: (value: string) => value,
      },
      {
        name: 'subcategory',
        render: (value: CategoryData, record: ProductData) => {
          return (
            <StyledMobileCategoryWrapper>
              {isAdmin && <span>{record.farm?.brand}</span>}
              <span>{value.name}</span>
            </StyledMobileCategoryWrapper>
          );
        },
      },
    ],
    bodyFields: [
      {
        name: 'product_amounts',
        label: intl.formatMessage({ id: 'products.available_amount' }),
        render: (productAmounts: ProductAmount[], record: ProductData) =>
          renderAvailableAmounts(productAmounts, record),
      },
    ],
    bodyTripleSectionFields: [
      {
        name: 'visibility',
        label: intl.formatMessage({ id: 'products.visibility' }),
        render: (value: boolean) => renderBooleanCheckmarks(!value),
      },
      {
        name: 'emarket',
        label: intl.formatMessage({ id: 'products.e_market' }),
        render: (value: boolean) => renderBooleanCheckmarks(value),
      },
      {
        name: 'wholesale',
        label: intl.formatMessage({ id: 'general.wholesale' }),
        render: (value: boolean) => renderBooleanCheckmarks(value),
      },
    ],
    footerFields: [
      {
        name: 'actions',
        render: (value: undefined, record: ProductData) => {
          function handleActionMenuClick(
            menuInfo: MenuInfo,
            record: ProductData
          ) {
            switch (menuInfo.key) {
              case 'edit':
                setSelectedProduct(record);
                setIsOpenCreteDrawer(true);
                break;
              case 'delete':
                if (
                  window.confirm(
                    `${intl.formatMessage({ id: 'confirmation.delete' })} : ${
                      record.name
                    }`
                  )
                ) {
                  deleteProd(null, { id: record.id });
                  setReload((old) => old + 1);
                }
                break;
              default:
                break;
            }
          }

          const dropdownTableItems: MenuProps['items'] = [];

          if (hasAbility(user, 'edit_product')) {
            dropdownTableItems.push({
              label: intl.formatMessage({ id: 'products.edit' }),
              key: 'edit',
            });
          }

          if (hasAbility(user, 'edit_product')) {
            dropdownTableItems.push({
              label: intl.formatMessage({ id: 'products.delete' }),
              key: 'delete',
              danger: true,
            });
          }

          if (dropdownTableItems.length === 0) {
            return null;
          }

          const menuPropsTable: MenuProps = {
            items: dropdownTableItems,
            onClick: (info) => handleActionMenuClick(info, record),
          };

          return (
            <Dropdown menu={menuPropsTable}>
              <Button
                label={intl.formatMessage({
                  id: 'general.actions',
                })}
                icon={<DownOutlined />}
                iconPosition={'end'}
                size={'small'}
                className={'actions'}
              />
            </Dropdown>
          );
        },
      },
    ],
  };

  const productsFilter = (
    <Filter
      onFinish={setAdvancedSearch}
      totalRecord={paginationStatus.total ?? 0}
      defaultFilterParam={stableFilter}
      reloadFilterTag={reload}
    >
      <CategorySelector displayFlex admin />
      <Input
        label={intl.formatMessage({ id: 'general.search' })}
        name="search"
      />
      {isAdmin && (
        <Select
          name={'farm_id'}
          className={'admin_farms'}
          placeholder={intl.formatMessage({ id: 'general.farms' })}
          labelInValue={true}
          layout={'vertical'}
          label={intl.formatMessage({ id: 'general.farms' })}
        >
          {farms.map((farm: FarmData) => {
            return <SelectOption value={farm.id}>{farm.name}</SelectOption>;
          })}
        </Select>
      )}
      <Checkbox
        label={intl.formatMessage({ id: 'general.e-market' })}
        name="emarket"
      />
      <Checkbox
        label={intl.formatMessage({ id: 'general.wholesale' })}
        name="wholesale"
      />
      <div className={'button-section-container'}>
        <div className={'button-section-wrapper'}>
          <Button
            className={'mobile-button'}
            type="link"
            label={intl.formatMessage({ id: 'general.delete_filter' })}
            onClick={() => resetFilter()}
          />

          <Button
            className={'mobile-button'}
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={() => setIsProductMobileFilterOpen(false)}
          />

          <Button
            type={'primary'}
            label={
              isLgUp
                ? intl.formatMessage({ id: 'general.select' })
                : intl.formatMessage({ id: 'general.filter_action' })
            }
            htmlType={'submit'}
            onClick={() => setIsProductMobileFilterOpen(false)}
          />
        </div>
      </div>
    </Filter>
  );

  const items = [
    {
      key: 'all_products',
      label: intl.formatMessage({ id: 'navigation.products' }),
      children: (
        <ResponsiveWrapper
          desktopComponent={
            <>
              {productsFilter}
              <Table
                reload={reload}
                url={'/api/v2/products'}
                columns={columns}
                filter={stableFilter}
                options={{ view: 'admin' }}
                saveData={handleGetProducts}
                saveResponse={handleResponse}
                paginnationStatus={setPaginationStatus}
                scroll={{ x: 'max-content' }}
              />
            </>
          }
          mobileComponent={
            <MobileList
              className={'create-edit-product-list'}
              url={'/api/v2/products'}
              filter={stableFilter}
              paginnationStatus={(el: object) => {
                setPaginationStatus(el);
              }}
              options={{ view: 'admin' }}
              coverFields={mobileItems.coverFields}
              bodyTripleSectionFields={mobileItems.bodyTripleSectionFields}
              bodyFields={mobileItems.bodyFields}
              footerFields={mobileItems.footerFields}
              reload={reload}
            />
          }
        />
      ),
    },
  ];

  return (
    <>
      <StyledMobileHeaderWrapper>
        <Button
          type={'text'}
          label={`+ ${intl.formatMessage({ id: 'products.create' })}`}
          onClick={() => handleOpenCreateDrawer()}
          ability={'edit_product'}
          icon={
            <Icon
              name={'admin/products-white-no-opacity'}
              className="admin-header-item-icon"
            />
          }
        />
        <Button
          type={'text'}
          label={intl.formatMessage({ id: 'general.filter' })}
          onClick={() => setIsProductMobileFilterOpen(true)}
          icon={
            <Icon
              name={'admin-filter-icon'}
              className="admin-header-item-icon"
            />
          }
        />
      </StyledMobileHeaderWrapper>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          {
            title: intl.formatMessage({ id: 'navigation.beginning' }),
          },
          {
            title: intl.formatMessage({ id: 'navigation.products' }),
          },
        ]}
      >
        <DefaultLayout.PageContent>
          <StyledProductsPage>
            <StyledMobilePageTitle>
              <span>{intl.formatMessage({ id: 'products.my_products' })}</span>
            </StyledMobilePageTitle>
            <Tabs
              items={items}
              tabBarExtraContent={
                <StyledButtonList className={'hide-mobile'}>
                  <Button
                    label={intl.formatMessage({ id: 'products.create' })}
                    type={'primary'}
                    onClick={handleOpenCreateDrawer}
                    ability={'edit_product'}
                  />
                  {isMansLauksEnabled && !!farmId && (
                    <Button
                      label={intl.formatMessage({ id: 'products.mans_lauks' })}
                      type={'default'}
                      onClick={handleMansLauksClick}
                      ability={'edit_product'}
                    />
                  )}
                  {isMansLauksEnabled &&
                    !!farmId &&
                    user?.isMansLauksLinked && (
                      <Button
                        label={intl.formatMessage({
                          id: 'products.mans_lauks_unlink',
                        })}
                        type={'default'}
                        onClick={handleMansLauksUnlinkClick}
                        ability={'edit_product'}
                      />
                    )}
                </StyledButtonList>
              }
            />
            <DrawerShareProduct
              open={isOpenDrawer}
              onClose={handleCloseDrawer}
              children={
                <ShareProductForm
                  productId={selectedProduct?.id}
                  productSlug={selectedProduct?.slug}
                />
              }
              title={intl.formatMessage({ id: 'drawers.share_product' })}
              closeIcon={<Icon name={'global-close-icon'} />}
            />
            <DrawerCreateProduct
              open={isOpenCreateDrawer}
              onClose={handleCloseCreateDrawer}
              farmId={selectedProduct?.farm_id}
              closeDrawer={handleCloseCreateDrawer}
              productId={selectedProduct?.id}
              setReload={setReload}
              mansLauksProducts={mansLauksProducts}
              products={returnedProducts}
              farms={farms}
            />
            {isMansLauksEnabled && mansLauksProducts && returnedProducts && (
              <DrawerMansLauksProducts
                open={isOpenMansLauksDrawer}
                onClose={handleCloseMansLauksDrawer}
                mansLauksProducts={mansLauksProducts}
                products={returnedProducts}
                getMansLauksProducts={getMansLauksProducts}
                setReload={setReload}
                farmId={farmId}
              />
            )}
            <AdminMobileFilterDrawer
              isOpen={isProductMobileFilterOpen}
              onClose={() => setIsProductMobileFilterOpen(false)}
            >
              {productsFilter}
            </AdminMobileFilterDrawer>
          </StyledProductsPage>
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
    </>
  );
};
